import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  ButtonGroup,
  HStack,
  VStack,
  Badge,
  Card,
  CardBody,
  SimpleGrid,
  Progress,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FaChartBar,
  FaChartLine,
  FaChartArea,
  FaChartPie,
  FaEquals,
} from 'react-icons/fa';
import {
  FiTrendingUp,
  FiTrendingDown,
} from 'react-icons/fi';

const EnhancedChartComponent = ({ data, title, width, height }) => {
  const [chartType, setChartType] = useState('enhanced-bar');
  const [activeSeries, setActiveSeries] = useState(null);

  // Color scheme
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const calculateGrowth = (currentMonth, categories, data) => {
    const currentIndex = categories.indexOf(currentMonth);
    if (currentIndex === 0) return 0;

    const prevMonth = categories[currentIndex - 1];
    const currentData = data.filter(item => item.name === currentMonth);
    const prevData = data.filter(item => item.name === prevMonth);

    const currentTotal = (currentData[0]?.data || 0) + (currentData[1]?.data || 0);
    const prevTotal = (prevData[0]?.data || 0) + (prevData[1]?.data || 0);

    if (prevTotal === 0) return 0;
    return ((currentTotal - prevTotal) / prevTotal) * 100;
  };


  // Process data
  const categories = [...new Set(data.map(item => item.name))];
  const processedData = categories.map(month => {
    const monthData = data.filter(item => item.name === month);
    const premium = monthData[0]?.data || 0;
    const freeTrial = monthData[1]?.data || 0;
    const total = premium + freeTrial;
    const premiumPercentage = total > 0 ? (premium / total) * 100 : 0;

    return {
      month,
      premium,
      freeTrial,
      total,
      premiumPercentage,
      growth: month !== categories[0] ? calculateGrowth(month, categories, data) : 0
    };
  });

  // Chart type options
  const chartTypes = [
    { id: 'enhanced-bar', label: 'Enhanced Bar', icon: FaChartBar },
    // { id: 'trend-line', label: 'Trend Analysis', icon: FaChartLine },
    // { id: 'stacked-progress', label: 'Progress View', icon: FaChartArea },
    { id: 'comparison-cards', label: 'Card View', icon: FaChartPie },
  ];

  // Enhanced Bar Chart Component
  const EnhancedBarChart = () => {
    const maxValue = Math.max(...processedData.map(d => d.total));

    return (
      <Box p={6}>
        <Flex justify="space-between" align="center" mb={6}>
          <VStack align="start" spacing={1}>
            <Text fontSize="lg" fontWeight="bold" color={textColor}>
              Monthly Customer Growth
            </Text>
            <Text fontSize="sm" color="gray.500">
              Premium vs Free Trial Distribution
            </Text>
          </VStack>

          <HStack spacing={4}>
            <HStack>
              <Box w={3} h={3} bg="#4F46E5" borderRadius="full" />
              <Text fontSize="sm" color={textColor}>Premium</Text>
            </HStack>
            <HStack>
              <Box w={3} h={3} bg="#06B6D4" borderRadius="full" />
              <Text fontSize="sm" color={textColor}>Free Trial</Text>
            </HStack>
          </HStack>
        </Flex>

        <HStack spacing={4} align="end" justify="space-around" h="300px">
          {processedData.map((item, index) => (
            <VStack key={item.month} spacing={2} align="center">
              <VStack spacing={1} align="center">
                <Text fontSize="sm" fontWeight="bold" color={textColor}>
                  {item.total}
                </Text>
                {item.growth !== 0 && (
                  <HStack spacing={1}>
                    {item.growth > 0 ? (
                      <FiTrendingUp color="#10B981" size={10} />
                    ) : item.growth < 0 ? (
                      <FiTrendingDown color="#EF4444" size={10} />
                    ) : (
                      <FaEquals color="#6B7280" size={10} />
                    )}
                    <Text
                      fontSize="xs"
                      color={item.growth > 0 ? "green.500" : item.growth < 0 ? "red.500" : "gray.500"}
                      fontWeight="medium"
                    >
                      {Math.abs(item.growth).toFixed(1)}%
                    </Text>
                  </HStack>
                )}
              </VStack>

              <VStack spacing={0} align="center">
                {/* Premium Bar */}
                <Tooltip label={`Premium: ${item.premium}`}>
                  <Box
                    w="40px"
                    h={`${(item.premium / maxValue) * 200}px`}
                    bg="linear-gradient(135deg, #4F46E5, #7C3AED)"
                    borderRadius="4px 4px 0 0"
                    cursor="pointer"
                    transition="all 0.3s ease"
                    _hover={{ transform: 'scaleX(1.1)', boxShadow: 'lg' }}
                    position="relative"
                    minH="4px"
                  >
                    {item.premium > 0 && (
                      <Text
                        position="absolute"
                        top="-20px"
                        left="50%"
                        transform="translateX(-50%)"
                        fontSize="xs"
                        fontWeight="bold"
                        color="#4F46E5"
                      >
                        {item.premium}
                      </Text>
                    )}
                  </Box>
                </Tooltip>

                {/* Free Trial Bar */}
                <Tooltip label={`Free Trial: ${item.freeTrial}`}>
                  <Box
                    w="40px"
                    h={`${(item.freeTrial / maxValue) * 200}px`}
                    bg="linear-gradient(135deg, #06B6D4, #0891B2)"
                    borderRadius="0 0 4px 4px"
                    cursor="pointer"
                    transition="all 0.3s ease"
                    _hover={{ transform: 'scaleX(1.1)', boxShadow: 'lg' }}
                    position="relative"
                    minH="4px"
                  >
                    {item.freeTrial > 0 && (
                      <Text
                        position="absolute"
                        bottom="-20px"
                        left="50%"
                        transform="translateX(-50%)"
                        fontSize="xs"
                        fontWeight="bold"
                        color="#06B6D4"
                      >
                        {item.freeTrial}
                      </Text>
                    )}
                  </Box>
                </Tooltip>
              </VStack>

              <Text fontSize="sm" fontWeight="medium" color={textColor}>
                {item.month}
              </Text>
            </VStack>
          ))}
        </HStack>
      </Box>
    );
  };

  // Trend Line Visualization
  const TrendLineChart = () => {
    return (
      <Box p={6}>
        <Text fontSize="lg" fontWeight="bold" color={textColor} mb={6}>
          Growth Trend Analysis
        </Text>

        <SimpleGrid columns={3} spacing={6} mb={6}>
          <Card>
            <CardBody>
              <VStack spacing={2}>
                <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                  {processedData.reduce((sum, item) => sum + item.total, 0)}
                </Text>
                <Text fontSize="sm" color="gray.500">Total Customers</Text>
              </VStack>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <VStack spacing={2}>
                <Text fontSize="2xl" fontWeight="bold" color="purple.500">
                  {processedData.reduce((sum, item) => sum + item.premium, 0)}
                </Text>
                <Text fontSize="sm" color="gray.500">Premium Users</Text>
              </VStack>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <VStack spacing={2}>
                <Text fontSize="2xl" fontWeight="bold" color="cyan.500">
                  {((processedData.reduce((sum, item) => sum + item.premium, 0) /
                    processedData.reduce((sum, item) => sum + item.total, 0)) * 100).toFixed(1)}%
                </Text>
                <Text fontSize="sm" color="gray.500">Conversion Rate</Text>
              </VStack>
            </CardBody>
          </Card>
        </SimpleGrid>

        <VStack spacing={4}>
          {processedData.map((item, index) => (
            <Card key={item.month} w="full">
              <CardBody>
                <Flex justify="space-between" align="center">
                  <HStack spacing={4}>
                    <Text fontSize="md" fontWeight="bold" color={textColor} minW="60px">
                      {item.month}
                    </Text>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" color="gray.500">
                        Total: {item.total} customers
                      </Text>
                      <HStack spacing={4}>
                        <Text fontSize="xs" color="purple.500">
                          Premium: {item.premium}
                        </Text>
                        <Text fontSize="xs" color="cyan.500">
                          Free: {item.freeTrial}
                        </Text>
                      </HStack>
                    </VStack>
                  </HStack>

                  <HStack spacing={4}>
                    <Progress
                      value={item.premiumPercentage}
                      colorScheme="purple"
                      size="lg"
                      w="150px"
                      borderRadius="full"
                    />
                    <Badge
                      colorScheme={item.growth > 0 ? "green" : item.growth < 0 ? "red" : "gray"}
                      variant="subtle"
                      px={2}
                      py={1}
                      borderRadius="full"
                    >
                      {item.growth > 0 && "+"}{item.growth.toFixed(1)}%
                    </Badge>
                  </HStack>
                </Flex>
              </CardBody>
            </Card>
          ))}
        </VStack>
      </Box>
    );
  };

  // Progress View
  const StackedProgressView = () => {
    const totalCustomers = processedData.reduce((sum, item) => sum + item.total, 0);

    return (
      <Box p={6}>
        <Text fontSize="lg" fontWeight="bold" color={textColor} mb={6}>
          Monthly Distribution Overview
        </Text>

        <VStack spacing={6}>
          {processedData.map((item) => (
            <Card key={item.month} w="full">
              <CardBody>
                <Flex justify="space-between" align="center" mb={4}>
                  <Text fontSize="md" fontWeight="bold" color={textColor}>
                    {item.month}
                  </Text>
                  <HStack spacing={4}>
                    <Text fontSize="sm" color="purple.500" fontWeight="medium">
                      Premium: {item.premium}
                    </Text>
                    <Text fontSize="sm" color="cyan.500" fontWeight="medium">
                      Free: {item.freeTrial}
                    </Text>
                    <Text fontSize="sm" color={textColor} fontWeight="bold">
                      Total: {item.total}
                    </Text>
                  </HStack>
                </Flex>

                <Box position="relative">
                  <Progress
                    value={100}
                    colorScheme="gray"
                    size="lg"
                    borderRadius="full"
                    bg="gray.100"
                  />
                  <Progress
                    value={(item.premium / item.total) * 100}
                    colorScheme="purple"
                    size="lg"
                    borderRadius="full"
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                  />

                  <HStack justify="space-between" mt={2}>
                    <Text fontSize="xs" color="purple.500">
                      {item.premiumPercentage.toFixed(1)}% Premium
                    </Text>
                    <Text fontSize="xs" color="cyan.500">
                      {(100 - item.premiumPercentage).toFixed(1)}% Free Trial
                    </Text>
                  </HStack>
                </Box>
              </CardBody>
            </Card>
          ))}
        </VStack>
      </Box>
    );
  };

  // Comparison Cards View
  const ComparisonCardsView = () => {
    return (
      <Box p={6}>
        <Text fontSize="lg" fontWeight="bold" color={textColor} mb={6}>
          Month-by-Month Comparison
        </Text>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {processedData.map((item) => (
            <Card key={item.month} position="relative" overflow="hidden">
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                h="4px"
                bg={`linear-gradient(90deg, #4F46E5 ${item.premiumPercentage}%, #06B6D4 ${item.premiumPercentage}%)`}
              />

              <CardBody pt={6}>
                <VStack spacing={4}>
                  <Text fontSize="xl" fontWeight="bold" color={textColor}>
                    {item.month}
                  </Text>

                  <SimpleGrid columns={2} spacing={4} w="full">
                    <VStack>
                      <Text fontSize="2xl" fontWeight="bold" color="purple.500">
                        {item.premium}
                      </Text>
                      <Text fontSize="xs" color="gray.500" textAlign="center">
                        Premium
                      </Text>
                    </VStack>

                    <VStack>
                      <Text fontSize="2xl" fontWeight="bold" color="cyan.500">
                        {item.freeTrial}
                      </Text>
                      <Text fontSize="xs" color="gray.500" textAlign="center">
                        Free Trial
                      </Text>
                    </VStack>
                  </SimpleGrid>

                  <Box w="full" textAlign="center">
                    <Text fontSize="sm" color={textColor} fontWeight="medium">
                      Total: {item.total}
                    </Text>
                    {item.growth !== 0 && (
                      <HStack justify="center" spacing={1} mt={1}>
                        {item.growth > 0 ? (
                          <FiTrendingUp color="#10B981" size={12} />
                        ) : (
                          <FiTrendingDown color="#EF4444" size={12} />
                        )}
                        <Text
                          fontSize="xs"
                          color={item.growth > 0 ? "green.500" : "red.500"}
                          fontWeight="medium"
                        >
                          {Math.abs(item.growth).toFixed(1)}%
                        </Text>
                      </HStack>
                    )}
                  </Box>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      </Box>
    );
  };

  const renderChart = () => {
    switch (chartType) {
      case 'enhanced-bar':
        return <EnhancedBarChart />;
      // case 'trend-line':
      //   return <TrendLineChart />;
      // case 'stacked-progress':
      //   return <StackedProgressView />;
      case 'comparison-cards':
        return <ComparisonCardsView />;
      default:
        return <EnhancedBarChart />;
    }
  };

  return (
    <Box w={width} h={height} bg={bgColor} borderRadius="xl" border="1px" borderColor={borderColor}>
      {/* Chart Type Selector */}
      <Box p={4} borderBottom="1px" borderColor={borderColor}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
          <Text fontSize="md" fontWeight="semibold" color={textColor}>
            {title}
          </Text>

          <ButtonGroup size="sm" isAttached variant="outline">
            {chartTypes.map((type) => {
              const Icon = type.icon;
              return (
                <Button
                  key={type.id}
                  onClick={() => setChartType(type.id)}
                  colorScheme={chartType === type.id ? "blue" : "gray"}
                  variant={chartType === type.id ? "solid" : "outline"}
                  leftIcon={<Icon />}
                  fontSize="xs"
                >
                  {type.label}
                </Button>
              );
            })}
          </ButtonGroup>
        </Flex>
      </Box>

      {/* Chart Content */}
      <Box h="calc(100% - 80px)" overflowY="auto">
        {renderChart()}
      </Box>
    </Box>
  );
};

export default EnhancedChartComponent;